// external libs
import path from 'path'
import { useLocation } from 'react-router-dom'
import { styled } from '@material-ui/styles'
import { makeStyles } from '@material-ui/core/styles'
import { Typography, SvgIcon, Box } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import Grid from '@material-ui/core/Grid'
import IconButton from '@material-ui/core/IconButton'
import Paper from '@material-ui/core/Paper'
import TextField from '@material-ui/core/TextField'

// components
import Dialog from '../../../../../control/dialog/dialog'

// hooks
import useAuthenStore from '../../../../../stores/authen/authenStore'
import useTranslate from '../../../../../i18n/useTranslate'
import useUploadFile from './useUploadFile'

// utils & constants
import { fileExt as fileExtConst } from '../../../../../constants/constants'
import { convertDateToFormatEN, convertDateToFormatTH } from '../../../../../utilities/date'

// assets
import { ReactComponent as DocumentIcon } from '../../../../../images/document.svg'

// types
import type { NewReturnListFileType, UploadFileType, UploadFileViewType } from './uploadFileType'

export type UplodeFileObjYype = {
    uploadFileObj: {
        documentTopicID: number
        fileList: {
            documentTopicID: number
            fileID: string
            fileToken: string
            filename: string
        }[]

        subTopicPageList?: {
            page: string
            documentTopicID: number
            subTopicID: number
        }[]
    }
}

const UploadFileView = (props: UploadFileViewType) => {
    const {
        dataSource, // type is DocumentTitleListType the main data
        disabled,
        required,
        name,
        field: { meta },
        refElementUploadEdit,
        refElementUpload,
        refElementTextField,
        refDialog,
        refDialogDownload,
        onClickUpload,
        onClickChange,
        onChangeUpload,
        onChangeUploadEdit,
        onClickClear,
        queryFileHistory,
        subTitleList,
        lstFileUpload = [],
        lstFileHistory,
        topicID,
        initialData,
        onChangeInputPage,
        disabledBtn = false,
        onClickOpenFile,
        accept = '.png,.jpeg,.tiff,.pdf,.doc,.docx',
        multipleFiles = true,
        renderLabel,
    } = props

    const translate = useTranslate()
    const isError = !disabled && meta.error && meta.touched ? true : false
    const classes = useStyles(isError)
    const { profile } = useAuthenStore()
    const isPreventUpload = !multipleFiles && lstFileUpload.length >= 1
    const { pathname } = useLocation()
    const isDontWantHistory = pathname.includes('settingDocument') || pathname.includes('officer')

    const renderIcon = (filename: any) => {
        const fileExt = path.extname(`${filename || ''}`)
        let iconPath = ''
        switch (fileExt) {
            case fileExtConst.pdf:
                iconPath = '/img/icons/pdf.png'
                break
            case fileExtConst.doc:
                iconPath = '/img/icons/word.png'
                break
            case fileExtConst.docx:
                iconPath = '/img/icons/word.png'
                break
            case fileExtConst.tiff:
                iconPath = '/img/icons/other_docs.png'
                break
            case fileExtConst.png:
                iconPath = '/img/icons/other_docs.png'
                break
            case fileExtConst.jpg:
                iconPath = '/img/icons/other_docs.png'
                break
            case fileExtConst.xls:
                iconPath = '/img/icons/excel.png'
                break
            case fileExtConst.xlsx:
                iconPath = '/img/icons/excel.png'
                break
            default:
                iconPath = '/img/icons/pdf.png'
                break
        }

        return (
            <img
                src={process.env.PUBLIC_URL + iconPath}
                alt="pdf"
                style={{ width: 24, height: 24, margin: 4, marginRight: 10 }}
            />
        )
    }

    const getDisplayDate = (val: any) => {
        let display = ''
        if (val) {
            display =
                translate.languageStore.currentLanguage === translate.languageKey.th
                    ? 'ล่าสุดวันที่ ' + convertDateToFormatTH(val)
                    : 'Last updated ' + convertDateToFormatEN(val)
        }
        return display
    }

    const getDisplayHistoryDate = (val: any) => {
        let display = '-'
        if (val) {
            display =
                translate.languageStore.currentLanguage === translate.languageKey.th
                    ? convertDateToFormatTH(val)
                    : convertDateToFormatEN(val)
        }
        return display
    }

    const bindingDialogRef = (func: Record<string, Function>) => {
        refDialog.current = { ...func }
    }
    const handleOpenDialog = async (fileID: any, replaceFileID: any) => {
        refDialog?.current?.openDialog()
        await queryFileHistory(fileID, replaceFileID)
    }
    const handleCloseDialog = () => {
        refDialog?.current?.closeDialog()
    }

    const bindingDialogDownloadRef = (func: Record<string, Function>) => {
        refDialogDownload.current = { ...func }
    }
    const handleOpenDialogDownload = async () => {
        refDialogDownload?.current?.openDialog()
    }

    const handleCloseDialogDownload = () => {
        refDialogDownload?.current?.closeDialog()
    }

    return (
        <FormControl
            style={{
                width: '100%',
            }}
        >
            <Grid container>
                <Grid container item md={10} sm={9} xs={12} justify={'flex-start'}>
                    {renderLabel ?? (
                        <Typography variant={'body2'}>
                            {dataSource?.no}
                            {dataSource?.no ? '.' : ''} {dataSource?.code}{' '}
                            {translate.languageStore.currentLanguage === translate.languageKey.th
                                ? dataSource?.name_th
                                : dataSource?.name_en}{' '}
                            <label style={{ color: 'red' }}>{dataSource?.isRequired ? `* ` : ''}</label>
                            {profile?.userGroupId === 1 && (dataSource?.file_name_th || dataSource?.file_name_en) && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={handleOpenDialogDownload}
                                >
                                    {translate.get('LABEL.DOWNLOAD_DOC')}
                                </Button>
                            )}
                        </Typography>
                    )}
                </Grid>

                {!disabledBtn && (
                    <Grid container item md={2} sm={3} xs={12} justify={'flex-end'} alignItems={'center'}>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={multipleFiles ? disabled : isPreventUpload}
                            onClick={onClickUpload}
                            size={'small'}
                            style={{ height: '30px' }}
                        >
                            {translate.get('BUTTON.UPLOAD')}
                        </Button>
                    </Grid>
                )}

                <Grid container item xs={12}>
                    <Paper
                        variant="outlined"
                        style={{ width: '100%', padding: '10px 15px 15px 15px', margin: '5px', marginRight: '0px' }}
                    >
                        {/* Has uploaded file */}
                        {lstFileUpload?.map((itm: NewReturnListFileType, index: number) => {
                            return (
                                <Grid key={itm.fileID} item xs={12}>
                                    <Grid
                                        container
                                        style={{
                                            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                                            padding: '5px 0',
                                        }}
                                    >
                                        <Grid container item xs={10} alignItems="center">
                                            {renderIcon(itm.filename)}

                                            <Typography variant={'body2'} style={{ wordBreak: 'break-word' }}>
                                                {(onClickOpenFile && (
                                                    <div
                                                        className={classes.div_a}
                                                        onClick={() => {
                                                            onClickOpenFile(itm.path ?? '', itm.filename)
                                                        }}
                                                    >
                                                        {dataSource?.code} {itm.filename}
                                                    </div>
                                                )) || (
                                                    <a href={itm.path} target="_blank" rel="noreferrer">
                                                        {dataSource?.code}
                                                        {itm.filename}
                                                    </a>
                                                )}
                                            </Typography>
                                            <Typography
                                                variant={'body2'}
                                                style={{ padding: '0 10px', wordBreak: 'break-word' }}
                                            >
                                                {getDisplayDate(itm.date)}
                                            </Typography>

                                            {itm.replaceFileID && !isDontWantHistory && (
                                                <IconButton
                                                    onClick={() => {
                                                        handleOpenDialog(itm.fileID, itm.replaceFileID)
                                                    }}
                                                    size="small"
                                                >
                                                    <SvgIcon fontSize="small" component={DocumentIcon} />
                                                </IconButton>
                                            )}
                                        </Grid>

                                        {!disabled && (
                                            <Grid container item xs={2} alignItems="center" justify={'flex-end'}>
                                                {!itm.isNewFile ? (
                                                    <Box display="flex" style={{ gap: 8 }}>
                                                        <Button
                                                            variant="contained"
                                                            className={classes.editfileBtn}
                                                            color="primary"
                                                            onClick={() => {
                                                                onClickChange(index)
                                                            }}
                                                            size={'small'}
                                                            style={{ height: '30px', right: '5px' }}
                                                        >
                                                            {translate.get('BUTTON.EDIT')}
                                                        </Button>

                                                        <Button
                                                            variant="contained"
                                                            color="secondary"
                                                            size={'small'}
                                                            onClick={() => {
                                                                onClickClear(itm)
                                                            }}
                                                            style={{ height: '30px', right: '5px' }}
                                                        >
                                                            {translate.get('BUTTON.DELETE')}
                                                        </Button>
                                                    </Box>
                                                ) : (
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        onClick={() => {
                                                            onClickClear(itm)
                                                        }}
                                                        size={'small'}
                                                        style={{ height: '30px', right: '5px' }}
                                                    >
                                                        {translate.get(
                                                            itm.replaceFileID ? 'BUTTON.CANCEL' : 'BUTTON.DELETE',
                                                        )}
                                                    </Button>
                                                )}
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                            )
                        })}

                        {/* No file uploaded */}
                        {lstFileUpload && lstFileUpload.length === 0 && (
                            <Typography variant={'body2'} style={{ paddingTop: '5px', wordBreak: 'break-word' }}>
                                {translate.get('LABEL.NO_FILE_UPLOAD')}
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                {lstFileUpload && lstFileUpload.length > 0 && subTitleList && subTitleList.length > 0 && (
                    <Grid item container xs={12} style={{ margin: 10 }} spacing={1}>
                        <Grid item container xs={11} justify={'flex-start'}>
                            <Typography variant={'body2'}>{translate.get('LABEL.PLEASE_FILL_IN_ADDRESS')}</Typography>
                        </Grid>
                        <Grid item container xs={1} justify={'flex-end'}>
                            <Typography variant={'body2'}>{translate.get('LABEL.PAGE')}</Typography>
                        </Grid>

                        {subTitleList?.map((itm: any, index: number) => {
                            let uploadObj = []
                            let initialSubtopic = []
                            if (initialData.length > 0) {
                                uploadObj = initialData?.filter(
                                    (itm: UplodeFileObjYype) => itm.uploadFileObj.documentTopicID === topicID,
                                )
                                initialSubtopic = uploadObj?.[0].uploadFileObj.subTopicPageList
                            }

                            return (
                                <>
                                    <Grid container item xs={11} justify={'flex-start'}>
                                        <Typography variant={'body2'}>
                                            {itm.no}
                                            {'. '}
                                            {translate.languageStore.currentLanguage === translate.languageKey.th
                                                ? itm.name_th
                                                : itm.name_en}
                                        </Typography>
                                    </Grid>
                                    <Grid container item xs={1} justify={'flex-end'}>
                                        <TextField
                                            style={{ width: '100%' }}
                                            defaultValue={
                                                initialSubtopic && initialSubtopic[index]
                                                    ? initialSubtopic[index].page
                                                    : ''
                                            }
                                            variant={'outlined'}
                                            size={'small'}
                                            disabled={disabled}
                                            InputProps={{
                                                classes: {
                                                    input: classes.inputPage,
                                                },
                                            }}
                                            onChange={(e: any) => {
                                                onChangeInputPage(e.target.value, itm.id, dataSource?.id)
                                            }}
                                        />
                                    </Grid>
                                </>
                            )
                        })}
                    </Grid>
                )}
            </Grid>

            <input
                type="file"
                accept="image/png, image/jpeg,image/tiff,.pdf,.doc,.docx"
                name={name}
                id={name}
                ref={refElementUploadEdit}
                onChange={(e) => {
                    onChangeUploadEdit(e, topicID)
                }}
                onClick={(event) => {
                    //@ts-ignore
                    event.currentTarget.value = null
                }}
                hidden
            />

            <input
                type="file"
                accept={accept}
                name={name}
                id={name}
                multiple={multipleFiles}
                ref={refElementUpload}
                onChange={(e) => {
                    onChangeUpload(e, topicID, accept)
                }}
                onClick={(event) => {
                    //@ts-ignore
                    event.currentTarget.value = null
                }}
                hidden
            />

            <TextField
                style={{ display: 'none' }}
                inputRef={refElementTextField}
                fullWidth
                required={required}
                disabled={disabled}
                variant="outlined"
                autoComplete="off"
                size="small"
                error={isError}
                InputLabelProps={{
                    shrink: true,
                }}
                InputProps={{
                    classes: {
                        root: classes.input,
                        adornedEnd: classes.button_end_icon,
                    },
                    endAdornment: (
                        <Grid
                            container
                            justify="flex-start"
                            alignContent="center"
                            style={{ width: '135px', flexWrap: 'nowrap' }}
                        ></Grid>
                    ),
                    readOnly: true,
                }}
                type="text"
            />

            <Dialog
                maxWidth="xs"
                onRef={bindingDialogDownloadRef}
                type=""
                title={translate.get('LABEL.DOWNLOAD_REC_DOC')}
                content={
                    <>
                        <Grid container justifyContent="center" spacing={1}>
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={6}>
                                        <Typography variant={'body2'} color="textSecondary">
                                            {translate.get('LABEL.LANGUAGE_THAI')}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Grid container justifyContent="flex-end">
                                            <Grid item>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    disabled={!dataSource?.file_name_th}
                                                    onClick={() => {
                                                        window.open(
                                                            `${dataSource?.file_path}/${dataSource?.file_name_th}` ||
                                                                '',
                                                        )
                                                    }}
                                                    size={'small'}
                                                    style={{ height: '30px' }}
                                                >
                                                    {translate.get('BUTTON.DOWNLOAD_BUTTON')}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={6}>
                                        <Typography variant={'body2'} color="textSecondary">
                                            {translate.get('LABEL.LANGUAGE_ENG')}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Grid container justifyContent="flex-end">
                                            <Grid item>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    disabled={!dataSource?.file_name_en}
                                                    onClick={() => {
                                                        window.open(
                                                            `${dataSource?.file_path}/${dataSource?.file_name_en}` ||
                                                                '',
                                                        )
                                                    }}
                                                    size={'small'}
                                                    style={{ height: '30px' }}
                                                >
                                                    {translate.get('BUTTON.DOWNLOAD_BUTTON')}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs="auto">
                                <pre />
                                <CustomButton onClick={handleCloseDialogDownload}>
                                    {translate.get('BUTTON.CLOSE_MODAL')}
                                </CustomButton>
                            </Grid>
                        </Grid>
                    </>
                }
            />

            <Dialog
                maxWidth="lg"
                onRef={bindingDialogRef}
                type=""
                title={translate.get('LABEL.HISTORY_FILE_INFORMATION')}
                content={
                    <>
                        <Grid container justify="center" spacing={1}>
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary">
                                            {translate.get('LABEL.FILE_NAME')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={1}>
                                        <Typography variant="caption" color="textSecondary">
                                            {translate.get('LABEL.FILE_TIME')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography variant="caption" color="textSecondary">
                                            {translate.get('LABEL.FILE_UPLOAD_BY')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Typography variant="caption" color="textSecondary">
                                            {translate.get('LABEL.FILE_UPLOAD_DATE')}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            {lstFileHistory &&
                                lstFileHistory?.map((item: any, inx: number) => (
                                    <Grid item xs={12} key={item.fileID}>
                                        <Grid container>
                                            <Grid container item xs={6} alignItems="center">
                                                {renderIcon(item.fileName)}
                                                <Typography variant={'body2'} style={{ wordBreak: 'break-word' }}>
                                                    <a href={item.path} target="_blank" rel="noreferrer">
                                                        {item.fileName}
                                                    </a>
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={1}>
                                                {lstFileHistory.length - inx}
                                            </Grid>
                                            <Grid item xs={3}>
                                                {item.updatedBy}
                                            </Grid>
                                            <Grid item xs={2}>
                                                {getDisplayHistoryDate(item.updatedDate)}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                ))}
                            <Grid item xs="auto">
                                <pre />
                                <CustomButton onClick={handleCloseDialog}>
                                    {translate.get('BUTTON.CLOSE_MODAL')}
                                </CustomButton>
                            </Grid>
                        </Grid>
                    </>
                }
            />

            <FormHelperText style={{ fontFamily: 'prompt', fontSize: '12px', color: '#ff4d4f' }}>
                {isError && meta.error}
            </FormHelperText>
        </FormControl>
    )
}

const CustomButton = styled(Button)({
    border: '1px solid #DB5F8E',
    boxShadow: '0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)',
})

const UploadFile = (props: UploadFileType) => {
    const uploadFileModel = useUploadFile(props)
    return <UploadFileView {...props} {...uploadFileModel} />
}

export default UploadFile

const useStyles = makeStyles((theme) => ({
    input: {
        '&>input': {
            cursor: 'default !important',
        },
    },
    button: {
        padding: '7px 16px',
        borderRadius: '0px 5px 5px 0px',
    },
    button_end_icon: {
        paddingRight: '0px',
    },
    clear_icon: {
        margin: theme.spacing(1),
    },
    label: {
        marginBottom: 10,
        color: (isError: any) => {
            if (isError) {
                return '#ff4d4f'
            } else {
                return ''
            }
        },
    },
    errorText: {
        fontSize: '12px',
        color: '#ff4d4f',
    },
    inputPage: {
        boxSizing: 'border-box',
        padding: '15px',
    },
    div_a: {
        color: 'blue',
        textDecoration: 'underline blue',
        cursor: 'pointer',
        '&:active': {
            color: 'red',
            textDecoration: 'underline red',
        },
    },
    multiLineEllipsis: {
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        '-webkit-line-clamp': 1,
        '-webkit-box-orient': 'vertical',
    },
    editfileBtn: {
        backgroundColor: '#e57600',
        color: '#fff',
        height: '44px',
        marginRight: '1px',
        '&:hover': {
            backgroundColor: '#ff8300',
        },
    },
}))
